/**
 * Conferência das etapas (E147.F3, GUS-180, ADR-0101), na pré-análise eletrônica.
 *
 * As sete etapas da raia eletrônica lidas do servidor, na ordem: o resumo diz onde está a primeira
 * pendência e a condição de saída dela; a tabela mostra a conferência da saída e a liberação de cada
 * etapa, com a ação principal da que não liberou e os critérios por expansão; a consulta do sistema
 * indisponível vem com as duas saídas da equipe. A conferência que não rodou agora não libera nada,
 * e a tela diz isso. Nada aqui é pedido ao cliente.
 */
import type { AxiosInstance } from 'axios';
import { useState } from 'react';

import { Blueprint, Button } from '@/components/ui';
import {
  consultasTravadas,
  primeiraPendente,
  type EtapasDoCasoData,
} from '@/lib/conferencia_das_etapas';
import { useConferenciaDasEtapas, type UsoDasEtapas } from '@/lib/useConferenciaDasEtapas';

import { ConsultaIndisponivel } from './ConsultaIndisponivel';
import { TabelaDasEtapas } from './TabelaDasEtapas';
import { preencher, t, textoDaCondicao } from './textos';

function Resumo({ dados }: { dados: EtapasDoCasoData }): JSX.Element {
  const pendente = primeiraPendente(dados);
  const titulo =
    pendente === null
      ? t.resumo.liberadas
      : preencher(t.resumo.pendente, { etapa: t.etapa[pendente.etapa] });
  const texto =
    pendente === null
      ? t.resumoLiberadas
      : textoDaCondicao(pendente.agora?.condicao_de_saida ?? null);
  return (
    <div
      className="border border-accent-200 bg-accent-bg p-sc-4"
      data-testid="etapas-resumo"
      data-pendente={pendente?.etapa ?? ''}
    >
      <p className="text-body-md font-semibold text-fg">{titulo}</p>
      <p className="mt-1 text-body-sm text-fg-muted">{texto}</p>
    </div>
  );
}

function Pronta({
  dados,
  caseId,
  pedir,
  onMudou,
}: {
  dados: EtapasDoCasoData;
  caseId: string;
  pedir: UsoDasEtapas['pedir'];
  onMudou?: (() => void) | undefined;
}): JSX.Element {
  const [aviso, setAviso] = useState<string | null>(null);
  const consultas = consultasTravadas(dados);
  if (!dados.conferida) {
    return (
      <p role="alert" className="text-body-sm text-danger-700" data-testid="etapas-nao-conferidas">
        {t.naoConferida}
      </p>
    );
  }
  return (
    <>
      <Resumo dados={dados} />
      <TabelaDasEtapas etapas={dados.etapas} caseId={caseId} />
      <p role="status" className="text-body-sm text-fg" data-testid="etapas-aviso">
        {aviso ?? ''}
      </p>
      {consultas.length > 0 ? (
        <section aria-labelledby="etapas-consultas-titulo" className="space-y-sc-2">
          <h4 id="etapas-consultas-titulo" className="text-body-md font-semibold text-fg">
            {t.consultasTitulo}
          </h4>
          <p className="text-body-sm text-fg-muted">{t.consultasTexto}</p>
          <ul className="space-y-sc-2">
            {consultas.map((c) => (
              <ConsultaIndisponivel
                key={c.provedor}
                consulta={c}
                pedir={pedir}
                onFeito={(texto) => {
                  setAviso(texto);
                  onMudou?.();
                }}
              />
            ))}
          </ul>
        </section>
      ) : null}
    </>
  );
}

export interface ConferenciaDasEtapasProps {
  client: AxiosInstance;
  caseId: string;
  /** Muda quando o caso é relido (exame, documento novo): as etapas são lidas de novo. */
  releitura?: unknown;
  /** O pedido da equipe mudou o caso (a tentativa verifica de novo): a página relê o caso. */
  onMudou?: (() => void) | undefined;
}

export function ConferenciaDasEtapas({
  client,
  caseId,
  releitura,
  onMudou,
}: ConferenciaDasEtapasProps): JSX.Element {
  const { etapas, reler, pedir } = useConferenciaDasEtapas(client, caseId, releitura);
  return (
    <Blueprint
      as="section"
      className="mt-sc-6 space-y-sc-4 p-sc-6"
      data-testid="conferencia-das-etapas"
      data-estado={etapas.estado}
      aria-labelledby="conferencia-das-etapas-titulo"
    >
      <div>
        <h3 id="conferencia-das-etapas-titulo" className="text-body-lg text-fg">
          {t.titulo}
        </h3>
      </div>
      {etapas.estado === 'carregando' ? (
        <p className="text-body-sm text-fg-muted">{t.carregando}</p>
      ) : null}
      {etapas.estado === 'erro' ? (
        <div role="alert" className="space-y-sc-2" data-testid="etapas-erro">
          <p className="text-body-sm text-danger-700">
            {etapas.motivo === 'sem_permissao' ? t.semPermissao : t.erro}
          </p>
          {etapas.motivo === 'indisponivel' ? (
            <Button type="button" variant="secondary" size="sm" onClick={reler}>
              {t.tentarDeNovo}
            </Button>
          ) : null}
        </div>
      ) : null}
      {etapas.estado === 'pronto' ? (
        <Pronta dados={etapas.dados} caseId={caseId} pedir={pedir} onMudou={onMudou} />
      ) : null}
    </Blueprint>
  );
}
